import { Button, IconButton, Menu, MenuItem, MenuSub } from '@affine/component';
import { useNewDoc } from '@affine/core/hooks/use-new-doc';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { DocsService } from '@affine/core/modules/doc';
import { EditorSettingService } from '@affine/core/modules/editor-setting';
import { TemplateListMenuContentScrollable } from '@affine/core/modules/template-doc/view/template-list-menu';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { useI18n } from '@affine/i18n';
import track from '@affine/track';
import {
  ArrowDownSmallIcon,
  EdgelessIcon,
  PageIcon,
  // PlusIcon,
  TemplateIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import IconFont from '@ys/repo-ui/base/IconFont';
import clsx from 'clsx';
import type React from 'react';
import { type MouseEvent, useCallback } from 'react';

import * as styles from './index.css';

// Re-export the common hook for backward compatibility
export { useNewDoc } from '@affine/core/hooks/use-new-doc';

interface AddPageButtonProps {
  className?: string;
  style?: React.CSSProperties;
}

const sideBottom = { side: 'bottom' as const };
export function AddPageButton(props: AddPageButtonProps) {
  const editorSetting = useService(EditorSettingService);
  const newDocDefaultMode = useLiveData(
    editorSetting.editorSetting.settings$.selector(s => s.newDocDefaultMode)
  );

  return newDocDefaultMode === 'ask' ? (
    <AddPageWithAsk {...props} />
  ) : (
    <AddPageWithoutAsk {...props} />
  );
}

function AddPageWithAsk({ className, style }: AddPageButtonProps) {
  const t = useI18n();
  const createDoc = useNewDoc();
  const workbench = useService(WorkbenchService).workbench;
  const docsService = useService(DocsService);

  const createPage = useCallback(
    (e?: MouseEvent) => {
      console.log('createPage');
      createDoc(e, 'page');
      track.$.navigationPanel.$.createDoc();
      track.$.sidebar.newDoc.quickStart({ with: 'page' });
    },
    [createDoc]
  );
  const createEdgeless = useCallback(
    (e?: MouseEvent) => {
      createDoc(e, 'edgeless');
      track.$.navigationPanel.$.createDoc();
      track.$.sidebar.newDoc.quickStart({ with: 'edgeless' });
    },
    [createDoc]
  );

  const createDocFromTemplate = useAsyncCallback(
    async (templateId: string) => {
      const docId = await docsService.duplicateFromTemplate(templateId);
      workbench.openDoc(docId);
      track.$.sidebar.newDoc.quickStart({ with: 'template' });
    },
    [docsService, workbench]
  );

  return (
    <Menu
      items={
        <>
          <MenuItem
            prefixIcon={<PageIcon />}
            onClick={createPage}
            onAuxClick={createPage}
          >
            {t['Page']()}
          </MenuItem>
          <MenuItem
            prefixIcon={<EdgelessIcon />}
            onClick={createEdgeless}
            onAuxClick={createEdgeless}
          >
            {t['Edgeless']()}
          </MenuItem>
          <MenuSub
            triggerOptions={{
              prefixIcon: <TemplateIcon />,
            }}
            subContentOptions={{
              sideOffset: 16,
              className: styles.templateMenu,
            }}
            items={
              <TemplateListMenuContentScrollable
                onSelect={createDocFromTemplate}
              />
            }
          >
            {t['Template']()}
          </MenuSub>
        </>
      }
    >
      <Button
        tooltip={t['New Page']()}
        tooltipOptions={sideBottom}
        data-testid="sidebar-new-page-with-ask-button"
        className={clsx([styles.withAskRoot, className])}
        style={style}
      >
        <div className={styles.withAskContent}>
          <IconFont name="tianjia" size={24} className={styles.addIcon} />
          <ArrowDownSmallIcon />
        </div>
      </Button>
    </Menu>
  );
}

function AddPageWithoutAsk({ className, style }: AddPageButtonProps) {
  const createDoc = useNewDoc();

  const onClickNewPage = useCallback(
    (e?: MouseEvent) => {
      createDoc(e);
      track.$.navigationPanel.$.asset_item_click({
        click_event: 'create_new_doc',
        parent_doc_id: '',
      });
    },
    [createDoc]
  );

  const t = useI18n();

  return (
    // <Menu
    //   items={
    //     <MenuItem
    //       type={'default'}
    //       prefixIcon={<PageIcon />}
    //       onClick={onClickNewPage}
    //     >
    //       {t['New Page']()}
    //     </MenuItem>
    //   }
    // >
    <IconButton
      tooltip={t['New Page']()}
      tooltipOptions={sideBottom}
      data-testid="sidebar-new-page-button"
      style={style}
      className={clsx([styles.root, className])}
      size={24}
      onClick={onClickNewPage}
      // onClick={onClickNewPage}
      // onAuxClick={onClickNewPage}
    >
      <IconFont name="tianjia" size={24} className={styles.addIcon} />
    </IconButton>
    // </Menu>
  );
}
